import { useQuery as useQueryReact } from '@tanstack/react-query';
import agent from '@root/agent';
import { SeverityWithText } from '@root/components/bootstrapTable/badges/severity';
import Loader from '@root/components/common/Loader';
import CountTooltip from '@root/components/table/cell-types/CountTooltip';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';
import { capitalizeFirstLetter } from '@root/utilities/string';
import CisaKevAlert from './CisaKevAlert';
import DescriptionToggle from './DescriptionToggle';
import FixedInVersion from './FixedInVersion';
import LinkToggle from './LinkToggle';
import { vulnerabilityDetailsQueryKey } from './vulnerabilityDetailsQuery';

// The "Details" tab of a CVE side overlay: description, packages, CVSS 3.1 metrics, threat trend and
// references. Values come from the clicked row first, then from the CVE lookup by `cve_id`. Shared by the
// workload Vulnerabilities overlay, the Kubernetes CVE overlay and the Kubernetes Findings overlay.
//
// `vulnerabilityDetails` (optional): a caller that already fetched the CVE from its own API passes the response
// here, and the `get_vulnerability_details` lookup is skipped. `null` counts as passed (nothing found); leave it
// undefined to fetch.

const formatVlue = v => {
  if (typeof v === 'string' && v?.includes('Updating')) return '-';
  else return v;
};

const CveDetails = ({ selectedRow, vulnerabilityDetails: providedVulnerabilityDetails }) => {
  const usesProvidedDetails = providedVulnerabilityDetails !== undefined;
  const { data: fetchedVulnerabilityDetails, isFetching: isFetchingVulnerabilityDetails } = useQueryReact(
    vulnerabilityDetailsQueryKey(selectedRow?.cve_id),
    () => agent.Vulnerabilites.get_vulnerability_details({ cve_id: selectedRow?.cve_id }),
    { enabled: !usesProvidedDetails && Boolean(selectedRow?.cve_id) },
  );
  const vulnerabilityDetails = usesProvidedDetails ? providedVulnerabilityDetails : fetchedVulnerabilityDetails;

  if (!usesProvidedDetails && isFetchingVulnerabilityDetails) return <Loader />;

  return (
    <div style={{ padding: '0px 6px' }}>
      <DescriptionToggle
        text={
          selectedRow?.details?.['Vulnerability Description'] || selectedRow?.vulnerability_details?.description || '--'
        }
      />
      <div style={{ padding: '20px', overflow: 'auto' }} className="workload CustomScrollbar">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: '1', padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Packages
            </div>

            <div
              className="text font-14 weight-500"
              style={{
                marginTop: '5px',
                color: 'var(--gray-900) !important',
                display: 'flex',
                alignItems: 'center',
                gap: '1px',
                lineHeight: '19.12px',
              }}>
              {Array.isArray(selectedRow?.packages) && selectedRow?.packages[0] ? (
                <>
                  {selectedRow.packages.length === 1 ? (
                    <span className="text value">{selectedRow.packages[0]}</span>
                  ) : (
                    <CountTooltip
                      showFirst={true}
                      fontWeight={'500'}
                      fontSize={'14px'}
                      labels={[...selectedRow.packages]}
                      visibleLabelStyle={{ width: '100%' }}
                      borderless
                    />
                  )}
                </>
              ) : (
                <span className="text value">-</span>
              )}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: '1', padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Published Date:
            </div>
            <div
              className="text font-14"
              style={{
                marginTop: '5px',
                color: 'var(--gray-900) !important',
                lineHeight: '19.12px',
                fontWeight: '500 !important',
              }}>
              {selectedRow?.threat_trend_data?.['Date Added']
                ? DateFormatPipe(selectedRow?.threat_trend_data?.['Date Added'])
                : vulnerabilityDetails?.data?.threat_trend_data?.['Date Added']
                  ? DateFormatPipe(vulnerabilityDetails?.data?.threat_trend_data?.['Date Added'])
                  : selectedRow?.elastic_data?.published
                    ? DateFormatPipe(selectedRow?.elastic_data?.published)
                    : '-'}
            </div>
          </div>
          <div style={{ flex: '1', padding: '0 10px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Source:
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.source ??
                vulnerabilityDetails?.data?.cvssMetricV31?.source ??
                selectedRow?.elastic_data?.source ??
                '-'}
            </div>
          </div>
          <div style={{ flex: '1', padding: '0 10px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Known Exploit:
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.cve_risk?.exploit === false || vulnerabilityDetails?.data?.cve_risk?.exploit === false
                ? 'False'
                : 'Yes'}
            </div>
          </div>
          <div style={{ flex: '1', padding: '0 10px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              EPSS:
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {(
                selectedRow?.details?.cve_details?.epss?.epss_percentile ??
                vulnerabilityDetails?.data?.cvssMetricV31?.epss_percentile ??
                selectedRow?.elastic_data?.epss ??
                0
              )?.toFixed?.(2)}
              %
            </div>
            {/* <div style={{ marginTop: '5px' }}>0.85</div> */}
          </div>
        </div>
        <div style={{ flex: '1' }}>
          <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
            CISA KEV
          </div>

          <div
            className="text font-14 weight-500"
            style={{
              marginTop: '5px',
              color: 'var(--gray-900) !important',
              display: 'flex',
              alignItems: 'center',
              gap: '1px',
              lineHeight: '19.12px',
            }}>
            {selectedRow?.details?.cisa_data ? (
              <CisaKevAlert data={selectedRow?.details?.cisa_data} />
            ) : vulnerabilityDetails?.data?.cvssMetricV31?.cisa_data ? (
              <CisaKevAlert data={vulnerabilityDetails?.data?.cvssMetricV31?.cisa_data} />
            ) : (
              <span className="text value" style={{ marginBottom: '20px' }}>
                -
              </span>
            )}
          </div>
        </div>
        <div style={{ flex: '1' }}>
          <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
            Fixed In Versions
          </div>

          <div
            className="text font-14 weight-500"
            style={{
              marginTop: '5px',
              color: 'var(--gray-900) !important',
              display: 'flex',
              alignItems: 'center',
              gap: '1px',
              lineHeight: '19.12px',
            }}>
            {selectedRow?.fixed_in_version ? (
              <FixedInVersion data={selectedRow?.fixed_in_version} />
            ) : (
              <span className="text value" style={{ marginBottom: '20px' }}>
                -
              </span>
            )}
          </div>
        </div>
        <span
          className="text font-14"
          style={{
            position: 'relative',
            bottom: '7px',
            left: '2px',
            fontWeight: 600,
            lineHeight: '19.12px',
          }}>
          CVSS(3.1)
        </span>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Base Score
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--secondary-400) !important' }}>
              <span
                style={{
                  backgroundColor: '#dbeafe',
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '12px',
                  fontFamily: 'Manrope',
                  fontWeight: 600,
                  // border: '1px solid #CBD5E0',
                }}>
                {selectedRow?.cvss_score === 0
                  ? 0
                  : parseInt(selectedRow?.cvss_score) === 10
                    ? 10
                    : vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.find(
                        item => item.cvssData.version === '3.1' || item.cvssData.version === '3.0',
                      )?.cvssData?.baseScore ||
                      parseFloat(selectedRow?.cvss_score)?.toFixed(1) ||
                      selectedRow?.elastic_data?.cvss?.[0]?.metrics?.baseScore?.toFixed(1) ||
                      '-'}
              </span>
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Impact Score
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--secondary-400) !important' }}>
              {selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0] ? (
                <span
                  style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontFamily: 'Manrope',
                    fontWeight: 600,
                    // border: '1px solid #CBD5E0',
                  }}>
                  {selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.impactScore != null
                    ? parseFloat(selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.impactScore).toFixed(1)
                    : selectedRow?.elastic_data?.cvss?.[0]?.metrics?.impactScore?.toFixed(1) || '-'}
                </span>
              ) : selectedRow?.elastic_data?.cvss?.[0]?.metrics?.impactScore ? (
                <span
                  style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontFamily: 'Manrope',
                    fontWeight: 600,
                    // border: '1px solid #CBD5E0',
                  }}>
                  {selectedRow?.elastic_data?.cvss?.[0]?.metrics?.impactScore?.toFixed(1)}
                </span>
              ) : (
                '-'
              )}
            </div>
          </div>

          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              EPSS Score
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--secondary-400) !important' }}>
              {selectedRow?.threat_trend?.epss != null ? (
                <span
                  style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontFamily: 'Manrope',
                    fontWeight: 600,
                    // border: '1px solid #CBD5E0',
                  }}>
                  {parseFloat(selectedRow?.threat_trend?.epss).toFixed(1)}
                </span>
              ) : selectedRow?.elastic_data?.epss != null ? (
                <span
                  style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontFamily: 'Manrope',
                    fontWeight: 600,
                    // border: '1px solid #CBD5E0',
                  }}>
                  {parseFloat(selectedRow?.elastic_data?.epss).toFixed(1)}
                </span>
              ) : (
                '-'
              )}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Exploitability Score
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--secondary-400) !important' }}>
              {selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.exploitabilityScore != null ? (
                <span
                  style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontFamily: 'Manrope',
                    fontWeight: 600,
                    // border: '1px solid #CBD5E0',
                  }}>
                  {parseFloat(selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.exploitabilityScore).toFixed(1)}
                </span>
              ) : selectedRow?.elastic_data?.cvss?.[0]?.metrics?.exploitabilityScore != null ? (
                <span
                  style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontFamily: 'Manrope',
                    fontWeight: 600,
                    // border: '1px solid #CBD5E0',
                  }}>
                  {parseFloat(selectedRow?.elastic_data?.cvss?.[0]?.metrics?.exploitabilityScore)?.toFixed(1)}
                </span>
              ) : (
                '-'
              )}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Severity
            </div>
            <div style={{ marginTop: '5px' }}>
              <SeverityWithText propsValue={selectedRow?.severity} />
            </div>
            {/* <div style={{ marginTop: '5px' }}>-</div> */}
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Privileges Required
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.privilegesRequired
                ? selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.privilegesRequired
                    .toLowerCase()
                    .replace(/^./, char => char.toUpperCase())
                : '-'}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Confidentiality
            </div>

            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.confidentialityImpact
                ? vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.confidentialityImpact
                : selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.confidentialityImpact
                  ? selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.confidentialityImpact
                      .toLowerCase()
                      .replace(/^./, char => char.toUpperCase())
                  : '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Attack Vector
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.attackVector
                ? vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.attackVector
                    .toLowerCase()
                    .replace(/^./, char => char.toUpperCase())
                : selectedRow?.elastic_data?.attack_vector
                  ? selectedRow?.elastic_data?.attack_vector.toLowerCase().replace(/^./, char => char.toUpperCase())
                  : selectedRow?.details?.attack_vector
                    ? selectedRow?.details?.attack_vector.toLowerCase().replace(/^./, char => char.toUpperCase())
                    : '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              User Interaction
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.userInteraction
                ? vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.userInteraction
                    .toLowerCase()
                    .replace(/^./, char => char.toUpperCase())
                : selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.userInteraction
                  ? selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.userInteraction
                      .toLowerCase()
                      .replace(/^./, char => char.toUpperCase())
                  : '-'}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Attack Complexity
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.attackComplexity
                ? vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.attackComplexity
                    .toLowerCase()
                    .replace(/^./, char => char.toUpperCase())
                : selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.attackComplexity
                  ? selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.attackComplexity
                      .toLowerCase()
                      .replace(/^./, char => char.toUpperCase())
                  : '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Scope
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.scope
                ? vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.scope
                    .toLowerCase()
                    .replace(/^./, char => char.toUpperCase())
                : selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.scope
                  ? selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.scope
                      .toLowerCase()
                      .replace(/^./, char => char.toUpperCase())
                  : '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Availability
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.availabilityImpact
                ? vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.availabilityImpact
                    .toLowerCase()
                    .replace(/^./, char => char.toUpperCase())
                : selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.availabilityImpact
                  ? selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.availabilityImpact
                      .toLowerCase()
                      .replace(/^./, char => char.toUpperCase())
                  : '-'}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Hackerone Count
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {formatVlue(
                selectedRow?.threat_trend_data?.hackerone_count ??
                  vulnerabilityDetails?.data?.threat_trend_data?.hackerone_count ??
                  '-',
              )}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Hackerone Rank
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {formatVlue(
                selectedRow?.threat_trend_data?.hackerone_rank ??
                  vulnerabilityDetails?.data?.threat_trend_data?.hackerone_rank ??
                  '-',
              )}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Is POC
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {JSON.stringify(
                selectedRow?.threat_trend_data?.is_poc ?? vulnerabilityDetails?.data?.threat_trend_data?.is_poc,
              ) ?? '-'}
            </div>
          </div>
        </div>{' '}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Is OSS
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {JSON.stringify(
                selectedRow?.threat_trend_data?.is_oss ?? vulnerabilityDetails?.data?.threat_trend_data?.is_oss,
              ) ?? '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Is Remote
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {JSON.stringify(
                selectedRow?.threat_trend_data?.is_remote ?? vulnerabilityDetails?.data?.threat_trend_data?.is_remote,
              ) ?? '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Is Template
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {JSON.stringify(
                selectedRow?.threat_trend_data?.is_template ??
                  vulnerabilityDetails?.data?.threat_trend_data?.is_template,
              ) ?? '-'}
            </div>
          </div>
        </div>{' '}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Last Modified
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.threat_trend_data?.last_modified &&
              !selectedRow?.threat_trend_data?.last_modified?.includes('Updating')
                ? DateFormatPipe(selectedRow?.threat_trend_data?.last_modified)
                : vulnerabilityDetails?.data?.threat_trend_data?.last_modified &&
                    !vulnerabilityDetails?.data?.threat_trend_data?.last_modified?.includes('Updating')
                  ? DateFormatPipe(vulnerabilityDetails?.data?.threat_trend_data?.last_modified)
                  : selectedRow?.elastic_data?.last_seen
                    ? DateFormatPipe(selectedRow?.elastic_data?.last_seen)
                    : '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Weakness
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.details?.cve_details?.weakness ??
                vulnerabilityDetails?.data?.threat_trend_data?.weaknesses_type?.[0] ??
                '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              First Seen
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.first_seen ? DateFormatPipe(selectedRow?.first_seen) : '-'}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Authentication
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.confidentialityImpact
                ? vulnerabilityDetails?.data?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.confidentialityImpact
                    .toLowerCase()
                    .replace(/^./, char => char.toUpperCase())
                : selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.confidentialityImpact
                  ? selectedRow?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.confidentialityImpact
                      .toLowerCase()
                      .replace(/^./, char => char.toUpperCase())
                  : '-'}
            </div>
          </div>

          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Last Updated
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {vulnerabilityDetails?.data?.threat_trend_data?.last_modified &&
              !vulnerabilityDetails?.data?.threat_trend_data?.last_modified?.includes('Updating')
                ? DateFormatPipe(vulnerabilityDetails?.data?.threat_trend_data?.last_modified)
                : selectedRow?.threat_trend_data?.last_modified &&
                    !selectedRow?.threat_trend_data?.last_modified?.includes('Updating')
                  ? DateFormatPipe(selectedRow?.threat_trend_data?.last_modified)
                  : '-'}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              OWASP Top 10
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.details?.['OWASP Top 10'] === false ? 'False' : 'True'}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Patch URL
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.details?.References && selectedRow.details.References.length > 0 ? (
                <LinkToggle links={selectedRow?.details?.References?.join?.(',')} />
              ) : selectedRow?.vulnerability_details?.reference_urls &&
                selectedRow?.vulnerability_details?.reference_urls?.length > 0 ? (
                <LinkToggle links={selectedRow?.vulnerability_details?.reference_urls?.join(',')} />
              ) : (
                '-'
              )}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Vendor Advisory
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.threat_trend_data?.vendor_advisory &&
              !selectedRow?.threat_trend_data?.vendor_advisory?.includes('Updating') ? (
                <LinkToggle links={selectedRow?.threat_trend_data?.vendor_advisory} />
              ) : vulnerabilityDetails?.data?.threat_trend_data?.vendor_advisory &&
                !vulnerabilityDetails?.data?.threat_trend_data?.vendor_advisory?.includes('Updating') ? (
                <LinkToggle links={vulnerabilityDetails?.data?.threat_trend_data?.vendor_advisory} />
              ) : (
                '-'
              )}
            </div>
          </div>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              SANS Top 25
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {selectedRow?.details?.['SANS Top 25'] === false ? 'False' : 'True'}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            textAlign: 'left',
            marginBottom: '20px',
          }}>
          <div style={{ flex: 1, padding: '0 5px' }}>
            <div className="text font-14" style={{ color: 'var(--secondary-300) !important', fontWeight: 600 }}>
              Vulnerability Status
            </div>
            <div
              className="text font-14 weight-500"
              style={{ marginTop: '5px', color: 'var(--gray-900) !important', lineHeight: '19.12px' }}>
              {capitalizeFirstLetter(
                formatVlue(
                  selectedRow?.threat_trend_data?.vuln_status ??
                    vulnerabilityDetails?.data?.threat_trend_data?.vuln_status,
                ) ?? '-',
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CveDetails;
